import { useCallback } from "react";
import { useSlideOperations } from "../context/InsertedSlidesContext";
export const usePlanHelpers = (plan) => {
    const { slideOperations, sectionOperations } = useSlideOperations();
    // Helper to find a slide in the plan
    const findSlide = useCallback((slideId) => {
        if (!plan)
            return null;
        for (const section of plan.sections) {
            const slide = section.slides.find(s => s.id === slideId);
            if (slide)
                return slide;
        }
        return null;
    }, [plan]);
    // Helper to find a section in the plan
    const findSection = useCallback((sectionId) => {
        if (!plan)
            return null;
        return plan.sections.find(s => s.id === sectionId) || null;
    }, [plan]);
    // Helper to check if a slide has pending changes (using flags)
    // ONLY returns true for LLM-initiated changes (source !== 'ui')
    // UI-initiated changes should NOT trigger styling
    const hasSlideChanges = useCallback((slideId) => {
        const slide = findSlide(slideId);
        if (!slide)
            return false;
        // Check pending flags (only LLM sets these)
        const hasPendingFlags = !!(slide._pendingDeletion || slide._pendingUpdate || slide._pendingInsertion);
        // Check reordered operations - only LLM-initiated ones (source !== 'ui')
        const hasLLMReorder = slideOperations.some(op => op.slideId === slideId && op.operation === 'reordered' && op.source !== 'ui');
        return hasPendingFlags || hasLLMReorder;
    }, [findSlide, slideOperations]);
    // Helper to check if a slide is deleted (using flags)
    // Only LLM sets _pendingDeletion flags
    const isSlideDeleted = useCallback((slideId) => {
        const slide = findSlide(slideId);
        return slide?._pendingDeletion === true;
    }, [findSlide]);
    // Helper to check if a slide is reordered
    // ONLY returns true for LLM-initiated reorders (source !== 'ui')
    const isSlideReordered = useCallback((slideId) => {
        return slideOperations.some(op => op.slideId === slideId && op.operation === 'reordered' && op.source !== 'ui');
    }, [slideOperations]);
    // Helper to get slide operation for a specific slide
    const getSlideOperation = useCallback((slideId) => {
        return slideOperations.find(op => op.slideId === slideId);
    }, [slideOperations]);
    // Helper to check if a section is reordered
    // ONLY returns true for LLM-initiated reorders (source !== 'ui')
    const isSectionReordered = useCallback((sectionId) => {
        return sectionOperations.some(op => op.sectionId === sectionId && op.operation === 'reordered' && op.source !== 'ui');
    }, [sectionOperations]);
    // Helper to check if a section is deleted (using flags)
    // Only LLM sets _pendingDeletion flags
    const isSectionDeleted = useCallback((sectionId) => {
        const section = findSection(sectionId);
        return section?._pendingDeletion === true;
    }, [findSection]);
    // Helper function to check if this is the divider above the first selected slide globally
    const isFirstSelectedSlideDivider = useCallback((sectionId, beforeSlideIndex, selectedSlides) => {
        if (!plan || selectedSlides.size === 0)
            return false;
        const sectionIndex = plan.sections.findIndex(s => s.id === sectionId);
        if (sectionIndex === -1)
            return false;
        const section = plan.sections[sectionIndex];
        // Check if the slide at this position is selected
        if (beforeSlideIndex >= section.slides.length || !selectedSlides.has(section.slides[beforeSlideIndex].id)) {
            return false;
        }
        // Check if there's any selected slide before this one in the entire plan
        for (let i = 0; i < sectionIndex; i++) {
            const prevSection = plan.sections[i];
            for (const slide of prevSection.slides) {
                if (selectedSlides.has(slide.id)) {
                    return false; // There's a selected slide in a previous section
                }
            }
        }
        // Check if there's any selected slide before this one in the current section
        for (let i = 0; i < beforeSlideIndex; i++) {
            if (selectedSlides.has(section.slides[i].id)) {
                return false; // There's an earlier selected slide in this section
            }
        }
        return true; // This is the first selected slide in the entire plan
    }, [plan]);
    return {
        findSlide,
        findSection,
        hasSlideChanges,
        isSlideDeleted,
        isSlideReordered,
        getSlideOperation,
        isSectionReordered,
        isSectionDeleted,
        isFirstSelectedSlideDivider,
    };
};
